import CloseIcon from "@mui/icons-material/Close";
import { Divider, Grid, IconButton } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import * as constants from "../../../constants";
import environment from "../../../environments/environment";
import CustomAutocomplete from "../../../shared/AutoComplete";
import ContainedButton from "../../../shared/Button/CustomContainedButton";
import OutlinedButton from "../../../shared/Button/customOutlinedButton";
import CustomTextField from "../../../shared/CustomTextFeild";
import { RootState } from "../../../store/store";
import { usePreviousMonths } from "../../fiscalUpgrade/CustomHooks/usePreviousMonths";
import { downloadParticipantMonthlyStatement } from "../../fiscalUpgrade/services/MonthlyCalculations/monthlyServices";
import { notifyUser } from "../../fiscalUpgrade/Utils/notifyUser";
type PopUpProps = {
  open: boolean;
  handleClose: () => void;
};

export default function ClientstatementReport(props: PopUpProps) {
  const { open, handleClose } = props;
  const participantDetail = useSelector(
    (state: RootState) => state.participantDetails
  );

  const userInfo = useSelector((state: RootState) => state.auth);

  const {
    montlyEndDateList,
    monthEndDate,
    fetchPreviousMonths,
    setMonthEndDate,
  } = usePreviousMonths(userInfo.clientId, constants.MONTHLY_LIST_NUMBER);
  const handleDialogClose = () => {
    handleClose();
    setMonthEndDate(monthEndDate);
  };
  const { t } = useTranslation();
  const translate = t as (key: string) => string;

  useEffect(() => {
    if (open) {
      fetchPreviousMonths();
    }
  }, [open]);
  async function onHandleGenerate() {
    if (!monthEndDate) {
      return;
    }
    const formatted = new Date(monthEndDate).toISOString().split("T")[0];
    try {
      const response = await downloadParticipantMonthlyStatement(formatted, participantDetail.participantId, constants.IS_ACTIVE_NO, constants.IS_ACTIVE_YES, userInfo.clientId);
      if (response && response.status === constants.STATUS_CODES.SUCCESS) {
        if (response.data.data === null) {
          notifyUser(
            response?.data?.message ?? translate("apiRequestFailed"),
            "error",
            translate("clientStatement")
          );
          handleClose();
          return;
        }

        if (response?.data?.data?.fileContent) {
          const base64String = response?.data?.data?.fileContent; // Ensure this is the actual base64 string
          const byteCharacters = atob(base64String);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: "application/pdf" });
          const link = document.createElement("a");
          link.href = URL.createObjectURL(blob);
          link.download = "client-statement-report.pdf";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          notifyUser(
            response?.data?.message ?? translate("actionSuccessful"),
            "success",
            translate("clientStatement")
          );
          setMonthEndDate(monthEndDate);
          handleClose();

          return;
        }
      } else {
        notifyUser(response?.response?.data?.message, "error", translate("clientStatement"));
        handleClose();
      }
    } catch (error: any) {
      handleClose();
      notifyUser(
        error?.response?.data?.message ??
        error.message ??
        translate("apiRequestFailed"),
        "error",
        translate("clientStatement")
      );
    }
  }

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth={false}
      sx={{
        maxWidth: "700px", // Adjust width as needed
        margin: "auto", // Center horizontally
        display: "flex", // Ensure flex behavior
        alignItems: "center", // Center vertically
        justifyContent: "center", // Center horizontally
        borderRadius: 2,
        "& .MuiPaper-root": { borderRadius: "12px", background: "white" },
      }}
    >
      {/* Dialog Header */}
      <DialogTitle
        sx={{
          backgroundColor: "#059ca9",
          color: "white",
          display: "flex",
          justifyContent: "space-between",
          padding: "10px 10px",
        }}
      >
        {translate("download")}
        <IconButton
          onClick={handleDialogClose}
          sx={{ color: "white", padding: "0px" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Dialog Content */}
      <DialogContent style={{ paddingTop: "20px" }}>
        <Grid container columnSpacing={2}>
          <Grid item xs={12} lg={6}>
            <CustomAutocomplete
              sx={{ width: "250px" }}
              labelText={translate("clientMonth")}
              options={montlyEndDateList}
              getOptionLabel={(option) => option.label || ""}
              value={
                montlyEndDateList.find((d: any) => d.value === monthEndDate) ||
                null
              }
              onChange={(event, newValue) => {
                setMonthEndDate(newValue?.value ?? "");
              }}
              slotProps={{
                paper: {
                  sx: { backgroundColor: "white !important" },
                },
              }}
              placeholder={translate("select")}
            />
          </Grid>

          <Grid item xs={12} lg={6}>
            <CustomTextField
              sx={{ width: "250px" }}
              labelText={translate("format")}
              readOnly
              value={translate("printablePdfFormat")}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <Divider />

      {/* Dialog Actions */}
      <DialogActions>
        <ContainedButton onClick={onHandleGenerate}>
          {translate("generate")}
        </ContainedButton>
        <OutlinedButton onClick={handleDialogClose}>
          {translate("cancel")}
        </OutlinedButton>
      </DialogActions>
    </Dialog>
  );
}
